// Animatie-jobs: een storyboard (+ voice-over) door Astra laten animeren op de Mac Studio,
// de voortgang volgen, "Klaar" melden als animator en het concept beoordelen.
//
// Plan: docs/astra-animatieflow-plan.md §5–§6 (stap 5). De wachtrij en de worker zitten
// erachter (/Api/animatie). Dit scherm is bewust klein: aanmaken, lijst, detail, review.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, Paper, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import toast from 'react-hot-toast';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';
import { leesJson } from '../../utils/apiFout';

const API = `${BACKEND_URL}/Api/animatie`;

const STATUS = {
  klaar: { label: 'In de wachtrij', color: 'default', uitleg: 'Wacht tot de Studio hem oppakt.' },
  geclaimd: { label: 'Wordt klaargezet', color: 'info', uitleg: 'De Studio haalt het pakket op.' },
  wacht_op_animator: { label: 'Wacht op animator', color: 'warning', uitleg: 'Log in als ff-animatie op de Studio en laat Astra bouwen. Klik daarna hier op Klaar.' },
  gemeld_klaar: { label: 'Klaar gemeld', color: 'info', uitleg: 'De worker gaat valideren.' },
  valideren: { label: 'Valideren', color: 'info', uitleg: 'Preview en project worden gecontroleerd en opgehaald.' },
  concept_geleverd: { label: 'Concept geleverd', color: 'success', uitleg: 'Bekijk de preview en beoordeel.' },
  mislukt: { label: 'Mislukt', color: 'error', uitleg: 'Zie de opmerking; opnieuw proberen kan.' },
  geannuleerd: { label: 'Geannuleerd', color: 'default', uitleg: '' },
};
const REVIEW = { concept: { label: 'Nog te beoordelen', color: 'default' }, goedgekeurd: { label: 'Goedgekeurd', color: 'success' }, afgekeurd: { label: 'Afgekeurd', color: 'error' } };
const tijd = (iso) => (iso ? new Date(iso).toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function AnimatieJobs() {
  const [jobs, setJobs] = useState([]);
  const [laden, setLaden] = useState(false);
  const [open, setOpen] = useState(null);      // job-detail
  const [nieuw, setNieuw] = useState(false);
  const [klanten, setKlanten] = useState([]);

  const laad = useCallback(async () => {
    setLaden(true);
    try {
      const d = await leesJson(await authFetch(`${API}/jobs?limit=100`), 'Jobs laden');
      setJobs(d.jobs || []);
    } catch (e) { toast.error(e.message); }
    finally { setLaden(false); }
  }, []);
  useEffect(() => { laad(); const t = setInterval(laad, 30000); return () => clearInterval(t); }, [laad]);
  useEffect(() => {
    (async () => {
      try {
        const d = await leesJson(await authFetch(`${BACKEND_URL}/Api/klanten`), 'Klanten laden');
        setKlanten((d.klanten || []).map((k) => ({ id: k.id, naam: k.fields?.Bedrijfsnaam || k.fields?.Naam || k.id })).sort((a, b) => a.naam.localeCompare(b.naam, 'nl')));
      } catch { /* klant is optioneel */ }
    })();
  }, []);

  const actief = useMemo(() => jobs.filter((j) => !['concept_geleverd', 'geannuleerd'].includes(j.status)), [jobs]);
  const klaarGeleverd = useMemo(() => jobs.filter((j) => ['concept_geleverd', 'geannuleerd'].includes(j.status)), [jobs]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="movie_filter"
        title="Animatie-jobs"
        subtitle="Storyboard en voice-over aanleveren, Astra laat het op de Studio in After Effects bouwen, de animator meldt Klaar en de regisseur beoordeelt het concept."
        accent={{ color: '#635983', bg: '#dbcdff' }}
        rightActions={(
          <>
            <Tooltip title="Vernieuwen"><IconButton size="small" onClick={laad}>{laden ? <CircularProgress size={18} /> : <RefreshIcon />}</IconButton></Tooltip>
            <Button variant="contained" size="small" onClick={() => setNieuw(true)} sx={{ textTransform: 'none' }}>Nieuwe animatie-job</Button>
          </>
        )}
      />

      <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Lopend ({actief.length})</Typography>
      {actief.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Geen lopende jobs.</Typography>}
      {actief.map((j) => <JobRij key={j.id} job={j} onOpen={() => setOpen(j.id)} />)}

      <Typography variant="subtitle2" sx={{ mt: 4, mb: 1 }}>Afgerond ({klaarGeleverd.length})</Typography>
      {klaarGeleverd.slice(0, 30).map((j) => <JobRij key={j.id} job={j} onOpen={() => setOpen(j.id)} />)}

      {nieuw && <NieuweJobDialog klanten={klanten} onClose={() => setNieuw(false)} onKlaar={() => { setNieuw(false); laad(); }} />}
      {open && <JobDetailDialog jobId={open} onClose={() => { setOpen(null); laad(); }} />}
    </Box>
  );
}

function JobRij({ job, onOpen }) {
  const st = STATUS[job.status] || { label: job.status, color: 'default' };
  return (
    <Paper variant="outlined" onClick={onOpen} sx={{
      p: 1.5, mb: 1, cursor: 'pointer', display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', gap: 2, alignItems: 'center',
      '&:hover': { bgcolor: 'action.hover' },
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>{job.soort === 'analyse' ? '📚 ' : ''}{job.titel}{job.projectNumber ? ` · #${job.projectNumber}` : ''}</Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {tijd(job.aangemaaktOp)} · {job.aangemaaktDoor?.split('@')[0]}{job.modus === 'zelf' ? ' · zelf' : ''}{job.stil ? ' · stil' : ''}{job.skills?.length ? ` · skills: ${job.skills.join(', ')}` : ''}
          {job.opmerking ? ` · ${job.opmerking}` : ''}
        </Typography>
      </Box>
      {job.opleveringen?.length > 0 && <Chip size="small" variant="outlined" label={`${job.opleveringen.length} oplevering${job.opleveringen.length === 1 ? '' : 'en'}`} />}
      <Chip size="small" label={st.label} color={st.color} />
    </Paper>
  );
}

function NieuweJobDialog({ klanten, onClose, onKlaar }) {
  const [vorm, setVorm] = useState({ titel: '', projectNumber: '', animatorEmail: '', stil: false, klant: null, modus: 'begeleid' });
  const [bestanden, setBestanden] = useState({ storyboard: null, voiceOver: null, assets: null, template: null });
  const [bezig, setBezig] = useState(false);
  const kies = (k) => (e) => setBestanden((b) => ({ ...b, [k]: e.target.files?.[0] || null }));

  const maak = async () => {
    if (!bestanden.storyboard) { toast.error('Storyboard (PDF) is verplicht'); return; }
    if (!bestanden.voiceOver && !vorm.stil) { toast.error('Voice-over is verplicht, of vink "stille film" aan'); return; }
    setBezig(true);
    try {
      const fd = new FormData();
      fd.append('storyboard', bestanden.storyboard);
      if (bestanden.voiceOver) fd.append('voice_over', bestanden.voiceOver);
      if (bestanden.assets) fd.append('assets', bestanden.assets);
      if (bestanden.template) fd.append('template', bestanden.template);
      fd.append('titel', vorm.titel);
      if (vorm.projectNumber) fd.append('projectNumber', vorm.projectNumber);
      if (vorm.animatorEmail) fd.append('animatorEmail', vorm.animatorEmail);
      if (vorm.klant) fd.append('klantId', vorm.klant.id);
      fd.append('stil', vorm.stil ? 'true' : 'false');
      fd.append('modus', vorm.modus);
      const r = await authFetch(`${API}/jobs`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail || d));
      toast.success(`Job ${d.job.id} staat in de wachtrij`);
      onKlaar();
    } catch (e) { toast.error(`Aanmaken mislukt: ${e.message}`); }
    finally { setBezig(false); }
  };

  const Bestand = ({ label, k, accept, hint }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Button variant="outlined" component="label" size="small" sx={{ textTransform: 'none', minWidth: 170 }}>
        {label}<input hidden type="file" accept={accept} onChange={kies(k)} />
      </Button>
      <Typography variant="caption" color={bestanden[k] ? 'text.primary' : 'text.secondary'} noWrap>{bestanden[k]?.name || hint}</Typography>
    </Box>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Nieuwe animatie-job</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '12px !important' }}>
        <TextField size="small" label="Titel" value={vorm.titel} onChange={(e) => setVorm((v) => ({ ...v, titel: e.target.value }))} placeholder="Bijv. LAN Handling – Expert Day loop" />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" label="Projectnummer" value={vorm.projectNumber} onChange={(e) => setVorm((v) => ({ ...v, projectNumber: e.target.value }))} sx={{ width: 160 }} />
          <TextField size="small" label="Animator (e-mail)" value={vorm.animatorEmail} onChange={(e) => setVorm((v) => ({ ...v, animatorEmail: e.target.value }))} fullWidth placeholder="wie mag Klaar melden; leeg = jijzelf" />
        </Box>
        <Autocomplete options={klanten} value={vorm.klant} onChange={(_, k) => setVorm((v) => ({ ...v, klant: k }))}
          getOptionLabel={(o) => o?.naam || ''} isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(p) => <TextField {...p} size="small" label="Klant (skill gaat mee)" />} />
        <Bestand label="Storyboard (PDF)" k="storyboard" accept=".pdf" hint="verplicht" />
        <Bestand label="Voice-over" k="voiceOver" accept=".mp3,.wav,.aif,.aiff,.m4a" hint={vorm.stil ? 'niet nodig bij een stille film' : 'verplicht — de timing volgt de audio'} />
        <FormControlLabel control={<Switch checked={vorm.stil} onChange={(e) => setVorm((v) => ({ ...v, stil: e.target.checked }))} />}
          label="Stille film (loop op een scherm): timing staat in het storyboard" />
        <Bestand label="Assets (zip)" k="assets" accept=".zip" hint="optioneel: logo's, illustraties, muziek" />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
          <Typography variant="body2" sx={{ minWidth: 110 }}>Waar bouwen?</Typography>
          <Chip label="Op de Studio (worker)" size="small" color={vorm.modus === 'begeleid' ? 'primary' : 'default'} variant={vorm.modus === 'begeleid' ? 'filled' : 'outlined'} onClick={() => setVorm((v) => ({ ...v, modus: 'begeleid' }))} />
          <Chip label="Zelf, op mijn eigen Mac" size="small" color={vorm.modus === 'zelf' ? 'primary' : 'default'} variant={vorm.modus === 'zelf' ? 'filled' : 'outlined'} onClick={() => setVorm((v) => ({ ...v, modus: 'zelf' }))} />
        </Box>
        <Bestand label="AE-template (.aep)" k="template" accept=".aep,.aepx" hint="optioneel: huisstijl en compositie-instellingen" />
        <Alert severity="info" sx={{ mt: 0.5 }}>
          {vorm.modus === 'zelf'
            ? <>Je downloadt straks het pakket (zip), geeft <code>OPDRACHT.md</code> en het storyboard aan Astra in je eigen ChatGPT Desktop met After Effects, en uploadt <code>preview.mp4</code> + <code>project.zip</code> hier terug. Wat terugkomt is een concept, nooit iets voor de klant.</>
            : <>Het pakket gaat naar de Studio. De animator logt daar in als <code>ff-animatie</code>, laat Astra het bouwen en meldt hier Klaar. Wat terugkomt is een concept, nooit iets voor de klant.</>}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button variant="contained" onClick={maak} disabled={bezig} sx={{ textTransform: 'none' }}>{bezig ? <CircularProgress size={18} /> : 'In de wachtrij zetten'}</Button>
      </DialogActions>
    </Dialog>
  );
}

function JobDetailDialog({ jobId, onClose }) {
  const [job, setJob] = useState(null);
  const [opmerking, setOpmerking] = useState('');
  const [upload, setUpload] = useState({ preview: null, project: null, opmerkingen: '', bezig: false });
  const laad = useCallback(async () => {
    try { const d = await leesJson(await authFetch(`${API}/jobs/${jobId}`), 'Job laden'); setJob(d.job); }
    catch (e) { toast.error(e.message); }
  }, [jobId]);
  useEffect(() => { laad(); const t = setInterval(laad, 15000); return () => clearInterval(t); }, [laad]);

  const actie = async (pad, body, tekst) => {
    try {
      const r = await authFetch(`${API}/${pad}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail || d));
      toast.success(tekst);
      laad();
    } catch (e) { toast.error(e.message); }
  };
  const annuleer = async () => {
    const ok = await bevestig({ titel: 'Job annuleren?', tekst: 'De Studio stopt ermee; het pakket blijft bewaard. Dit is niet terug te draaien.', knop: 'Annuleren', gevaarlijk: true });
    if (ok) actie(`jobs/${jobId}/annuleer`, { reden: 'via intern' }, 'Geannuleerd');
  };
  const beoordeel = async (opl, status) => {
    if (status === 'afgekeurd' && !opmerking.trim()) { toast.error('Zeg bij afkeuren wat er anders moet — daar leert de volgende ronde van'); return; }
    if (status === 'goedgekeurd') {
      const ok = await bevestig({ titel: 'Concept goedkeuren?', tekst: 'Het concept gaat dan naar de animator voor de definitieve versie. Nog niets gaat naar de klant.', knop: 'Goedkeuren' });
      if (!ok) return;
    }
    actie(`opleveringen/${opl.id}/beoordeel`, { status, opmerking }, status === 'goedgekeurd' ? 'Goedgekeurd' : 'Afgekeurd met opmerking');
  };

  const downloadPakket = async () => {
    // De route geeft een 302 naar een kortlopende link; met auth ophalen en dan de link openen.
    try {
      const r = await authFetch(`${API}/jobs/${jobId}/pakket`, { redirect: 'manual' });
      const link = r.headers.get('location');
      if (!link) { window.open(`${API}/jobs/${jobId}/pakket`, '_blank', 'noopener'); return; }
      window.open(link, '_blank', 'noopener');
    } catch (e) { toast.error(`Downloaden mislukt: ${e.message}`); }
  };
  const uploadResultaat = async () => {
    if (!upload.preview || !upload.project) { toast.error('preview.mp4 én project.zip zijn nodig'); return; }
    setUpload((u) => ({ ...u, bezig: true }));
    try {
      const fd = new FormData();
      fd.append('preview', upload.preview); fd.append('project', upload.project);
      if (upload.opmerkingen.trim()) fd.append('opmerkingen', upload.opmerkingen.trim());
      const r = await authFetch(`${API}/jobs/${jobId}/resultaat-upload`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === 'string' ? d.detail : JSON.stringify(d.detail || d));
      toast.success('Resultaat geüpload — concept staat klaar voor beoordeling');
      setUpload({ preview: null, project: null, opmerkingen: '', bezig: false });
      laad();
    } catch (e) { toast.error(`Uploaden mislukt: ${e.message}`); setUpload((u) => ({ ...u, bezig: false })); }
  };
  const naarSkill = async (opl) => {
    const ok = await bevestig({ titel: 'Learnings overnemen in de skill van de klant?', tekst: 'De huidige skill-tekst wordt vervangen door deze learnings (nieuwe versie; de vorige blijft in de historie).', knop: 'Overnemen' });
    if (ok) actie(`opleveringen/${opl.id}/naar-skill`, {}, 'Overgenomen in de skill van de klant');
  };
  const st = job ? (STATUS[job.status] || { label: job.status, color: 'default', uitleg: '' }) : null;
  const laatste = job?.opleveringen?.[0];
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {job ? job.titel : 'Job'} {st && <Chip size="small" label={st.label} color={st.color} />}
      </DialogTitle>
      <DialogContent>
        {!job && <CircularProgress size={22} />}
        {job && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{st.uitleg}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Job {job.id}{job.projectNumber ? ` · #${job.projectNumber}` : ''} · gemaakt {tijd(job.aangemaaktOp)} door {job.aangemaaktDoor}
              {job.animatorEmail ? ` · animator ${job.animatorEmail}` : ''}{job.stil ? ' · stille film' : ''}{job.skills?.length ? ` · skills: ${job.skills.join(', ')}` : ''}
              {job.workerId ? ` · Studio ${job.workerId.slice(0, 8)}` : ''}{job.voortgang?.pad ? ` · pakket: ${job.voortgang.pad}` : ''}
            </Typography>
            {job.opmerking && <Alert severity={job.status === 'mislukt' ? 'error' : 'info'} sx={{ mb: 2 }}>{job.opmerking}</Alert>}

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
              {job.status !== 'geannuleerd' && (
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadPakket} sx={{ textTransform: 'none' }}>Pakket downloaden (zip)</Button>
              )}
              {job.status === 'wacht_op_animator' && job.modus !== 'zelf' && (
                <Button variant="contained" startIcon={<DoneAllIcon />} sx={{ textTransform: 'none' }}
                  onClick={() => actie(`jobs/${jobId}/klaar`, {}, 'Klaar gemeld — de worker gaat valideren')}>
                  Klaar: Astra is klaar, uit/ staat vol
                </Button>
              )}
              {['klaar', 'geclaimd', 'wacht_op_animator'].includes(job.status) && (
                <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={annuleer} sx={{ textTransform: 'none' }}>Annuleren</Button>
              )}
              {job.status === 'mislukt' && (
                <Button variant="outlined" startIcon={<ReplayIcon />} onClick={() => actie(`jobs/${jobId}/opnieuw`, {}, 'Opnieuw in de wachtrij')} sx={{ textTransform: 'none' }}>Opnieuw proberen</Button>
              )}
            </Box>

            {job.modus === 'zelf' && ['wacht_op_animator', 'gemeld_klaar'].includes(job.status) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Zelf doen op je eigen Mac</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  1. Download het pakket en pak het uit. 2. Open ChatGPT Desktop (Astra) met After Effects erbij en sleep <code>OPDRACHT.md</code> en <code>storyboard.pdf</code> in het gesprek: "Animeer dit storyboard." 3. Upload hier <code>preview.mp4</code> en het AE-project als zip (Bestand → Afhankelijkheden → Verzamel bestanden, dan zippen).
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button variant="outlined" component="label" size="small" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none' }}>
                    {upload.preview ? upload.preview.name : 'preview.mp4'}<input hidden type="file" accept=".mp4,.mov" onChange={(e) => setUpload((u) => ({ ...u, preview: e.target.files?.[0] || null }))} />
                  </Button>
                  <Button variant="outlined" component="label" size="small" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none' }}>
                    {upload.project ? upload.project.name : 'project.zip'}<input hidden type="file" accept=".zip" onChange={(e) => setUpload((u) => ({ ...u, project: e.target.files?.[0] || null }))} />
                  </Button>
                  <TextField size="small" label="Opmerkingen van Astra (optioneel)" value={upload.opmerkingen} onChange={(e) => setUpload((u) => ({ ...u, opmerkingen: e.target.value }))} sx={{ flex: 1, minWidth: 220 }} />
                  <Button variant="contained" size="small" disabled={upload.bezig || !upload.preview || !upload.project} onClick={uploadResultaat} sx={{ textTransform: 'none' }}>
                    {upload.bezig ? <CircularProgress size={16} /> : 'Resultaat uploaden'}
                  </Button>
                </Box>
              </Paper>
            )}
            {job.opleveringen?.map((opl) => (
              <Paper key={opl.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2">Oplevering {opl.id} · {tijd(opl.op)}</Typography>
                  <Chip size="small" label={REVIEW[opl.status]?.label || opl.status} color={REVIEW[opl.status]?.color || 'default'} />
                  {opl.projectUrl && <Button size="small" href={opl.projectUrl} target="_blank" rel="noreferrer" sx={{ textTransform: 'none' }}>AE-project (zip)</Button>}
                </Box>
                {opl.manifest?.soort === 'analyse' ? (
                  <>
                    <Box component="pre" sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12.5, whiteSpace: 'pre-wrap', maxHeight: 420, overflow: 'auto' }}>{opl.manifest.learnings}</Box>
                    {opl.status === 'concept' && (
                      <Button variant="contained" size="small" onClick={() => naarSkill(opl)} sx={{ textTransform: 'none', mt: 1 }}>Overnemen in de skill van de klant</Button>
                    )}
                  </>
                ) : opl.previewUrl && (
                  <Box component="video" src={opl.previewUrl} controls preload="metadata" sx={{ width: '100%', maxHeight: 420, bgcolor: '#000', borderRadius: 1, display: 'block' }} />
                )}
                {opl.manifest?.preview && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {opl.manifest.preview.width}×{opl.manifest.preview.height} · {Math.round(opl.manifest.preview.duration || 0)} s · {opl.manifest.preview.codec}{opl.manifest.preview.audio ? ' · met audio' : ' · zonder audio'}
                  </Typography>
                )}
                {opl.manifest?.opmerkingen && (
                  <Box component="pre" sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>{opl.manifest.opmerkingen}</Box>
                )}
                {opl.status === 'concept' && opl.id === laatste?.id && opl.manifest?.soort !== 'analyse' && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField size="small" fullWidth multiline minRows={2} label="Opmerking (verplicht bij afkeuren)" value={opmerking} onChange={(e) => setOpmerking(e.target.value)} />
                    <Button variant="contained" color="success" startIcon={<CheckCircleIcon />} onClick={() => beoordeel(opl, 'goedgekeurd')} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>Goedkeuren</Button>
                    <Button variant="outlined" color="error" onClick={() => beoordeel(opl, 'afgekeurd')} sx={{ textTransform: 'none' }}>Afkeuren</Button>
                  </Box>
                )}
                {opl.status !== 'concept' && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {REVIEW[opl.status]?.label} door {opl.beoordeeldDoor} op {tijd(opl.beoordeeldOp)}{opl.opmerking ? ` — ${opl.opmerking}` : ''}
                  </Typography>
                )}
              </Paper>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} sx={{ textTransform: 'none' }}>Sluiten</Button></DialogActions>
    </Dialog>
  );
}
